import React, { useEffect, useState, useCallback } from "react";
import {
  MailOutlined,
  UserOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  FormOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import {
  Space,
  Row,
  Col,
  Typography,
  Card,
  Statistic,
  Progress,
  Empty,
  Divider,
  Button,
  Alert,
  Tag,
  List,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getReports } from "../../../redux/actions/userAction";
import { listCampaigns } from "../../../redux/actions/campaignsAction";

const { Title, Text, Paragraph } = Typography;

export default function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const recentCampaigns = useSelector((state) => state.campaigns.campaigns);
  const user = JSON.parse(localStorage.getItem("user"));

  const address = user?.address || {};
  const hasPassword =
    !!user?.hasPassword ||
    !!user?.passwordHash ||
    localStorage.getItem("hasPassword") === "true";
  const profileSteps = [
    {
      key: "name",
      label: "Add your name",
      done: !!(user?.firstName && user?.lastName),
    },
    {
      key: "phone",
      label: "Add your phone number",
      done: !!user?.phoneNumber,
    },
    {
      key: "company",
      label: "Add your company name",
      done: !!user?.companyName,
    },
    {
      key: "address",
      label: "Add your company address",
      done:
        !!address.streetAddress &&
        !!address.city &&
        !!address.pincode &&
        !!address.state &&
        !!address.country,
    },
    {
      key: "website",
      label: "Add your company website",
      done: !!user?.companyWebsite,
    },
    {
      key: "password",
      label: "Create a password",
      done: hasPassword,
    },
  ];
  const completedSteps = profileSteps.filter((s) => s.done).length;
  const profileCompletion =
    Math.round((completedSteps / profileSteps.length) * 100) || 0;
  const metricValueStyle = {
    fontFamily:
      'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
    fontWeight: 600,
    letterSpacing: 0.3,
  };

  const formatLimit = (limit) => (limit === -1 ? "Unlimited" : limit);

  const getUsagePercent = (used, limit) => {
    if (limit === -1) {
      if (!used) {
        return 0;
      }
      return Math.min(100, Math.round((used / (used + 10000)) * 100));
    }
    if (!limit || limit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const fetchReport = useCallback(() => {
    dispatch(getReports())
      .then((response) => {
        setData(response);
      })
      .catch((e) => console.log(e));
  }, [dispatch]);

  useEffect(() => {
    fetchReport();
    dispatch(listCampaigns({ pageSize: 5, pageNum: 1 })).catch((e) =>
      console.log(e),
    );
  }, [fetchReport, dispatch]);

  // First-run only: once the org has created anything, this permanently
  // stops showing rather than eating dashboard space forever.
  const isBrandNewOrg =
    data && data.totalCampaigns === 0 && data.totalContacts === 0;

  const quotaWarnings = data
    ? [
        {
          key: "emailsPerDay",
          label: "Daily email limit",
          percent: getUsagePercent(
            data.totalEmailsToday,
            data.limits.emailsPerDay,
          ),
        },
        {
          key: "emailsPerMonth",
          label: "Monthly email limit",
          percent: getUsagePercent(
            data.totalEmailsCurrentMonth,
            data.limits.emailsPerMonth,
          ),
        },
        {
          key: "contactsPerMonth",
          label: "Monthly contacts limit",
          percent: getUsagePercent(
            data.totalContactsCurrentMonth,
            data.limits.contactsPerMonth,
          ),
        },
        {
          key: "campaignsPerMonth",
          label: "Monthly campaigns limit",
          percent: getUsagePercent(
            data.totalCampaignsCurrentMonth,
            data.limits.campaignsPerMonth,
          ),
        },
      ].filter((m) => m.percent >= 80)
    : [];

  return (
    <div className="Home">
      <Space
        style={{
          display: "flex",
          margin: "0 0.5rem 30px 0.5rem",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Welcome Back, {user.emailAddress}
          </Title>
          <Text type="secondary">Dashboard Overview</Text>
        </div>
      </Space>
      <Row gutter={[30, 30]} align="stretch">
        {quotaWarnings.length > 0 && (
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              message="Approaching plan limits"
              description={quotaWarnings
                .map((w) => `${w.label}: ${w.percent}% used`)
                .join(" · ")}
            />
          </Col>
        )}
        {profileCompletion < 100 && (
          <Col xs={24}>
            <Card className="dashboard-card" size="small">
              <Space
                align="center"
                style={{ width: "100%", justifyContent: "space-between" }}
                wrap
              >
                <Space direction="vertical" size={4} style={{ minWidth: 240 }}>
                  <Text strong>
                    Your profile is {profileCompletion}% complete
                  </Text>
                  <Progress
                    percent={profileCompletion}
                    style={{ maxWidth: 320 }}
                    size="small"
                  />
                </Space>
                <Button onClick={() => navigate("/app/settings")}>
                  Finish setup
                </Button>
              </Space>
            </Card>
          </Col>
        )}
        {isBrandNewOrg && (
          <Col xs={24}>
            <Card
              title="Welcome to Mailinflow"
              className="dashboard-card"
              size="small"
            >
              <Paragraph style={{ marginBottom: 0 }}>
                Import your contacts, then create your first campaign to get
                started. This card goes away once you do.
              </Paragraph>
            </Card>
          </Col>
        )}
        {data && (
          <>
            <Col xs={24}>
              <Divider orientation="left"></Divider>
            </Col>

            <Col xs={24} md={8}>
              <Card
                title="Upcoming send"
                className="dashboard-card"
                size="small"
              >
                {data.nextScheduledCampaign ? (
                  <Space direction="vertical" size={4}>
                    <Text strong>{data.nextScheduledCampaign.subject}</Text>
                    <Space size={4}>
                      <ClockCircleOutlined />
                      <Text type="secondary">
                        {new Date(
                          data.nextScheduledCampaign.scheduledSendAt,
                        ).toLocaleString()}
                      </Text>
                    </Space>
                  </Space>
                ) : (
                  <Text type="secondary">No campaigns scheduled.</Text>
                )}
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                title={
                  <Space size={6}>
                    <FormOutlined />
                    Forms & leads
                  </Space>
                }
                className="dashboard-card"
                size="small"
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Text type="secondary">Submissions today</Text>
                    <Text strong>{data.forms.submissionsToday}</Text>
                  </Space>
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Text type="secondary">Submissions this month</Text>
                    <Text strong>{data.forms.submissionsCurrentMonth}</Text>
                  </Space>
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Text type="secondary">Flagged as spam this month</Text>
                    <Text strong>{data.forms.spamCurrentMonth}</Text>
                  </Space>
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                title={
                  <Space size={6}>
                    <RiseOutlined />
                    Engagement (last 30 days)
                  </Space>
                }
                className="dashboard-card"
                size="small"
              >
                {data.engagement ? (
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Space
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text type="secondary">Open rate</Text>
                      <Text strong>{data.engagement.openRate}%</Text>
                    </Space>
                    <Space
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Text type="secondary">Click rate</Text>
                      <Text strong>{data.engagement.clickRate}%</Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Across {data.engagement.campaignsCounted} campaign
                      {data.engagement.campaignsCounted === 1 ? "" : "s"} sent
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">
                    No campaigns sent in the last 30 days.
                  </Text>
                )}
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card className="dashboard-card">
                <Statistic
                  title="Emails today"
                  value={data.totalEmailsToday}
                  valueStyle={metricValueStyle}
                  suffix={
                    <Text className="mono-metric">
                      / {formatLimit(data.limits.emailsPerDay)}
                    </Text>
                  }
                  prefix={<MailOutlined />}
                />
                <Text type="secondary" className="metric-helper">
                  Usage:{" "}
                  {getUsagePercent(
                    data.totalEmailsToday,
                    data.limits.emailsPerDay,
                  )}
                  %
                </Text>
                <Progress
                  percent={getUsagePercent(
                    data.totalEmailsToday,
                    data.limits.emailsPerDay,
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card className="dashboard-card">
                <Statistic
                  title="Emails this month"
                  value={data.totalEmailsCurrentMonth}
                  valueStyle={metricValueStyle}
                  suffix={
                    <Text className="mono-metric">
                      / {formatLimit(data.limits.emailsPerMonth)}
                    </Text>
                  }
                  prefix={<MailOutlined />}
                />
                <Text type="secondary" className="metric-helper">
                  Usage:{" "}
                  {getUsagePercent(
                    data.totalEmailsCurrentMonth,
                    data.limits.emailsPerMonth,
                  )}
                  %
                </Text>
                <Progress
                  percent={getUsagePercent(
                    data.totalEmailsCurrentMonth,
                    data.limits.emailsPerMonth,
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card className="dashboard-card">
                <Statistic
                  title="Contacts added this month"
                  value={data.totalContactsCurrentMonth}
                  valueStyle={metricValueStyle}
                  suffix={
                    <Text className="mono-metric">
                      / {formatLimit(data.limits.contactsPerMonth)}
                    </Text>
                  }
                  prefix={<UserOutlined />}
                />
                <Text type="secondary" className="metric-helper">
                  Usage:{" "}
                  {getUsagePercent(
                    data.totalContactsCurrentMonth,
                    data.limits.contactsPerMonth,
                  )}
                  %
                </Text>
                <Progress
                  percent={getUsagePercent(
                    data.totalContactsCurrentMonth,
                    data.limits.contactsPerMonth,
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card className="dashboard-card">
                <Statistic
                  title="Campaigns this month"
                  value={data.totalCampaignsCurrentMonth}
                  valueStyle={metricValueStyle}
                  suffix={
                    <Text className="mono-metric">
                      / {formatLimit(data.limits.campaignsPerMonth)}
                    </Text>
                  }
                  prefix={<InboxOutlined />}
                />
                <Text type="secondary" className="metric-helper">
                  Usage:{" "}
                  {getUsagePercent(
                    data.totalCampaignsCurrentMonth,
                    data.limits.campaignsPerMonth,
                  )}
                  %
                </Text>
                <Progress
                  percent={getUsagePercent(
                    data.totalCampaignsCurrentMonth,
                    data.limits.campaignsPerMonth,
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} md={6}>
              <Card className="dashboard-card">
                <Statistic
                  title="Contacts"
                  value={data.totalContacts}
                  prefix={<UserOutlined />}
                  valueStyle={metricValueStyle}
                />
                <Text type="secondary">Total contacts added</Text>
              </Card>
            </Col>

            <Col xs={24} md={6}>
              <Card className="dashboard-card">
                <Statistic
                  title="Campaigns"
                  value={data.totalCampaigns}
                  prefix={<InboxOutlined />}
                  valueStyle={metricValueStyle}
                />
                <Text type="secondary">Total campaigns created</Text>
              </Card>
            </Col>

            <Col xs={24}>
              <Space wrap>
                <Button
                  icon={<UserOutlined />}
                  onClick={() => navigate("/app/contacts")}
                >
                  Import contacts
                </Button>
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  onClick={() => navigate("/app/campaigns")}
                >
                  Create campaign
                </Button>
              </Space>
            </Col>
          </>
        )}
        <Col xs={24} md={12}>
          <Card
            title="Recent campaigns"
            className="dashboard-card"
            extra={
              <Button type="link" onClick={() => navigate("/app/campaigns")}>
                View all
              </Button>
            }
          >
            {recentCampaigns.length ? (
              <List
                dataSource={recentCampaigns}
                renderItem={(c) => (
                  <List.Item>
                    <Space
                      direction="vertical"
                      size={0}
                      style={{ width: "100%" }}
                    >
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text strong>{c.subject}</Text>
                        {c.status && (
                          <Tag color={c.status.type}>
                            {c.status.description}
                          </Tag>
                        )}
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(c.createdAt).toLocaleString()}
                      </Text>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={<Paragraph>No campaigns yet.</Paragraph>}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
